import React, { useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/hooks/useAdDetails';
import { ProlongAdResponse } from '@/api/types/ads.types';
import { PROLONG_AD } from '@/api/mutations/ads.mutations';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { WarningModal } from '@/components/Ad/WarningModal';
import { Button } from '@/components/UI/Button/Button';
import { CommonTestIds } from '@/__e2e__/testIds/common';

type Props = {
  dataTestId?: string;
  onSuccess?: () => void;
};

export type ProlongByBalancePopupRef = {
  toggle: () => void;
};

export const ProlongByBalancePopup = React.forwardRef<
  ProlongByBalancePopupRef,
  Props
>(({ dataTestId, onSuccess }, ref) => {
  const { t } = useTranslation();
  const [isPopupOpen, togglePopup] = useToggleOpenState();
  const { categoryDetails } = useCurrentCategoryDetails();
  const [isLoading, setIsLoading] = useState(false);

  const { adDetails, refetch: refetchAdDetails } = useAdDetails();
  const [prolongAd] = useMutation<ProlongAdResponse>(PROLONG_AD, {
    onCompleted: data => {
      refetchAdDetails().then(() => {
        if (data.prolongAd.entity) {
          togglePopup(false);
          onSuccess?.();
          setIsLoading(false);
        }
      });
    },
  });

  const description = t('pages.ad.pay_by_balance_description', {
    categoryName: categoryDetails?.name,
  });

  useImperativeHandle(
    ref,
    () => ({
      toggle: togglePopup,
    }),
    []
  );

  function onConfirmClick() {
    if (adDetails) {
      setIsLoading(true);
      prolongAd({ variables: { adId: adDetails.id } });
    }
  }

  return (
    <WarningModal
      dataTestId={dataTestId ?? AdPageTestIds.popup.prolongByBalance}
      isOpen={isPopupOpen}
      onClose={() => togglePopup(false)}
      title={t('pages.ad.pay_by_balance')}
      description={description}
      actions={
        <>
          <Button
            data-testid={CommonTestIds.popup.actionLeft}
            appearance="secondaryOutlined"
            isFullWidth
            borderRadius={14}
            disabled={isLoading}
            onClick={() => togglePopup(false)}
          >
            {t('common.deny')}
          </Button>
          <Button
            data-testid={CommonTestIds.popup.actionRight}
            appearance="secondary"
            isFullWidth
            borderRadius={14}
            disabled={isLoading}
            onClick={onConfirmClick}
          >
            {t('common.confirm')}
          </Button>
        </>
      }
    />
  );
});

ProlongByBalancePopup.displayName = 'ProlongByBalancePopup';
